import type { EstimateOption } from "../api/pricing";

/**
 * Normalize a cash-network payer code for cross-corridor matching.
 * CRB publishes `MX_ELEKTRA`; INCOMM publishes `ELEKTRA` for the same network.
 */
export function normalizePayerCode(code: string | undefined | null): string {
  if (!code?.trim()) return "";
  let upper = code.trim().toUpperCase();
  if (/^[A-Z]{2}_/.test(upper)) {
    upper = upper.slice(3);
  }
  return upper
    .replace(/_WALMART$/, "")
    .replace(/_CASH$/, "")
    .replace(/^WALMART_BODEGA_AURRERA$/, "BODEGA_AURRERA");
}

export function payerCodesEquivalent(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  const left = a.trim().toUpperCase();
  const right = b.trim().toUpperCase();
  if (left === right) return true;
  return normalizePayerCode(left) === normalizePayerCode(right);
}

export function isAnyAnyCapability(capabilityKey: string | undefined | null): boolean {
  return /:ANY:ANY:/i.test(capabilityKey ?? "");
}

/** True when the capabilityKey state segment is missing or `ANY`. */
export function isWildcardStateCapability(
  capabilityKey: string | undefined | null
): boolean {
  if (isAnyAnyCapability(capabilityKey)) return true;
  const parts = capabilityKey?.split(":") ?? [];
  if (parts.length < 7) return false;
  const state = parts[4]?.toUpperCase();
  return !state || state === "ANY";
}

/** State segment of a capabilityKey (`US:MX:…:CASH_PICKUP:AGU:CITY:PAYER`). */
export function capabilityStateCode(
  capabilityKey: string | undefined | null
): string | null {
  const parts = capabilityKey?.split(":") ?? [];
  if (parts.length < 7) return null;
  const state = parts[4]?.toUpperCase();
  if (!state || state === "ANY") return null;
  return state;
}

/** Bare subdivision (`MX-AGU` / `AGU` → `AGU`) for capability matching. */
export function bareStateCode(stateCode: string | undefined | null): string | null {
  if (!stateCode?.trim()) return null;
  const trimmed = stateCode.trim().toUpperCase();
  const dash = trimmed.indexOf("-");
  if (dash > 0 && dash <= 3) return trimmed.slice(dash + 1) || null;
  return trimmed;
}

/**
 * Pick a single display payer code per receive network from offerable options.
 * Prefers ANY:ANY (INCOMM) codes over country-prefixed CRB spellings so the
 * requester does not see ELEKTRA and MX_ELEKTRA as two products.
 */
export function dedupeOfferableCashPayers(
  options: Array<Pick<EstimateOption, "payerCode" | "capabilityKey" | "payoutMethod">>
): string[] {
  type Candidate = { code: string; capabilityKey: string };
  const bestByNorm = new Map<string, Candidate>();

  for (const o of options) {
    if (o.payoutMethod !== "CASH_PICKUP" || !o.payerCode) continue;
    const norm = normalizePayerCode(o.payerCode);
    if (!norm) continue;
    const next: Candidate = {
      code: o.payerCode,
      capabilityKey: o.capabilityKey ?? "",
    };
    const prev = bestByNorm.get(norm);
    if (!prev) {
      bestByNorm.set(norm, next);
      continue;
    }
    bestByNorm.set(norm, preferCashPayerCandidate(prev, next));
  }

  return [...bestByNorm.values()].map((c) => c.code);
}

function preferCashPayerCandidate(
  a: { code: string; capabilityKey: string },
  b: { code: string; capabilityKey: string }
): { code: string; capabilityKey: string } {
  const aAny = isAnyAnyCapability(a.capabilityKey);
  const bAny = isAnyAnyCapability(b.capabilityKey);
  if (aAny !== bAny) return aAny ? a : b;
  const aPrefixed = /^[A-Z]{2}_/.test(a.code);
  const bPrefixed = /^[A-Z]{2}_/.test(b.code);
  if (aPrefixed !== bPrefixed) return aPrefixed ? b : a;
  return a.code.length <= b.code.length ? a : b;
}

/** Canonical catalog `displayName`, or null until the list can resolve it. */
export function catalogPayerDisplayName(
  code: string | null | undefined,
  payers:
    | Array<{ payerCode: string; displayName?: string }>
    | null
    | undefined
): string | null {
  if (!code?.trim() || !payers) return null;
  const found = payers.find((p) => payerCodesEquivalent(p.payerCode, code));
  const name = found?.displayName?.trim();
  return name ? name : null;
}

/**
 * Resolve the cash pre-quote option for mint: equivalent payer, prefer ANY:ANY,
 * else exact state match. Returns null when only state-scoped SKUs exist and
 * none cover the selected state (avoids minting MX_ELEKTRA+AGU wrongly).
 */
export function resolveCashPickupOption(
  options: EstimateOption[],
  payerCode: string,
  stateCode: string | null | undefined
): EstimateOption | null {
  const matches = options.filter(
    (o) =>
      o.payoutMethod === "CASH_PICKUP" &&
      payerCodesEquivalent(o.payerCode, payerCode)
  );
  if (matches.length === 0) return null;

  const wildcard = matches.find((o) =>
    isWildcardStateCapability(o.capabilityKey)
  );
  if (wildcard) return wildcard;

  const state = bareStateCode(stateCode);
  if (!state) return null;

  return (
    matches.find((o) => capabilityStateCode(o.capabilityKey) === state) ?? null
  );
}

/**
 * Cash payer codes that can actually price for `stateCode` — same gate as
 * continue / mint. Use for the state-scoped picker so we never show e.g.
 * MX_7ELEVEN for Aguascalientes when only JAL (or other) SKUs exist.
 */
export function dedupeOfferableCashPayersForState(
  options: EstimateOption[],
  stateCode: string | null | undefined
): string[] {
  if (!bareStateCode(stateCode)) return [];
  return dedupeOfferableCashPayers(options).filter(
    (code) => resolveCashPickupOption(options, code, stateCode) != null
  );
}
